/**
 * MyCockpitWidget - Tableau de bord personnel pour les roles internes (Carla, Julie, Amelie, CEO).
 * Vue agregee : projets/leads assignes, delais critiques, taches du jour, activite recente.
 *
 * Reception : GET /api/dashboard/me
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); } catch { return iso; }
};

const StatusPill = ({ status }) => {
  const map = {
    initiation: 'bg-blue-100 text-blue-700',
    acquisition: 'bg-purple-100 text-purple-700',
    notaire: 'bg-indigo-100 text-indigo-700',
    administratif: 'bg-amber-100 text-amber-700',
    travaux: 'bg-orange-100 text-orange-700',
    livraison: 'bg-teal-100 text-teal-700',
    termine: 'bg-green-100 text-green-700',
  };
  return <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
};

const MyCockpitWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard/me`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="realease-card p-6 animate-pulse" data-testid="my-cockpit-loading">
        <div className="h-4 bg-gray-200 rounded w-32 mb-3"></div>
        <div className="h-3 bg-gray-100 rounded w-48"></div>
      </div>
    );
  }

  if (!data) return null;

  const { projects, delays, tasks, leads, recent_activity } = data;
  const showLeads = leads !== null && leads !== undefined;
  const totalAlerts = (delays?.red_count || 0) + (delays?.yellow_count || 0);
  const totalTodo = (tasks?.overdue_count || 0) + (tasks?.today_count || 0);

  return (
    <div className="space-y-4" data-testid="my-cockpit">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-bold text-gray-900">Mon cockpit</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/notifications/daily-cockpit-me`);
                if (r.data?.sent) alert('Email envoye a ' + r.data.email);
                else alert('Email non envoye (verifiez RESEND_API_KEY ou votre email).');
              } catch (e) {
                alert('Echec : ' + (e.response?.data?.detail || e.message));
              }
            }}
            className="text-xs text-[#008A8D] hover:underline"
            data-testid="cockpit-email-test-btn"
            title="Recevoir une copie email de mon cockpit maintenant"
          >
            M'envoyer par email
          </button>
          <p className="text-xs text-gray-500">Mise a jour temps reel</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cockpit-stats">
        <a href="/projects" className="realease-card p-4 hover-lift" data-testid="cockpit-stat-projects">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Projets</p>
              <p className="text-2xl font-bold text-[#008A8D] mt-1">{projects?.count || 0}</p>
              <p className="text-[11px] text-gray-500 mt-1">
                {projects?.active || 0} actif{(projects?.active || 0) > 1 ? 's' : ''}
                {projects?.paused > 0 && <span className="text-orange-600"> · {projects.paused} en pause</span>}
              </p>
            </div>
            <svg className="w-5 h-5 text-[#008A8D]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" /></svg>
          </div>
        </a>

        {showLeads && (
          <a href="/crm" className="realease-card p-4 hover-lift" data-testid="cockpit-stat-leads">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Leads</p>
                <p className="text-2xl font-bold text-[#008A8D] mt-1">{leads.count || 0}</p>
                <p className="text-[11px] text-gray-500 mt-1">{leads.count === 0 ? 'Aucun' : 'a suivre'}</p>
              </div>
              <svg className="w-5 h-5 text-[#008A8D]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857" /></svg>
            </div>
          </a>
        )}

        <a href="/delais" className={`realease-card p-4 hover-lift ${delays?.red_count > 0 ? 'border-l-4 border-red-500' : ''}`} data-testid="cockpit-stat-delays">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Delais critiques</p>
              <p className={`text-2xl font-bold mt-1 ${delays?.red_count > 0 ? 'text-red-600' : delays?.yellow_count > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                {totalAlerts}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">
                {delays?.red_count > 0 && <span className="text-red-600 font-semibold">{delays.red_count} en retard</span>}
                {delays?.red_count > 0 && delays?.yellow_count > 0 && ' · '}
                {delays?.yellow_count > 0 && <span className="text-amber-600">{delays.yellow_count} dans 7j</span>}
                {totalAlerts === 0 && 'Tout va bien'}
              </p>
            </div>
            <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
        </a>

        <div className="realease-card p-4" data-testid="cockpit-stat-tasks">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Mes taches</p>
              <p className={`text-2xl font-bold mt-1 ${tasks?.overdue_count > 0 ? 'text-red-600' : 'text-[#008A8D]'}`}>
                {totalTodo}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">
                {tasks?.overdue_count > 0 && <span className="text-red-600 font-semibold">{tasks.overdue_count} en retard</span>}
                {tasks?.overdue_count > 0 && tasks?.today_count > 0 && ' · '}
                {tasks?.today_count > 0 && <span>{tasks.today_count} aujourd'hui</span>}
                {totalTodo === 0 && 'Aucune urgence'}
              </p>
            </div>
            <svg className="w-5 h-5 text-[#008A8D]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
          </div>
        </div>
      </div>

      {/* Two columns : Delais / Projets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Delais critiques */}
        <div className="realease-card p-5" data-testid="cockpit-delays">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Delais a traiter</h3>
            <a href="/delais" className="text-xs text-[#008A8D] hover:underline">Tout voir</a>
          </div>
          {delays?.items?.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {delays.items.map(d => (
                <a key={d.delay_id} href={`/delais?project_id=${d.project_id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors" data-testid={`cockpit-delay-${d.delay_id}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${d.alert === 'red' ? 'bg-red-500' : 'bg-amber-500'}`}></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.description || d.delay_type}</p>
                    <p className="text-[11px] text-gray-500 truncate">{d.project_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-xs font-bold ${d.alert === 'red' ? 'text-red-600' : 'text-amber-600'}`}>
                      {d.days_left < 0 ? `${Math.abs(d.days_left)}j retard` : d.days_left === 0 ? "Aujourd'hui" : `J-${d.days_left}`}
                    </p>
                    <p className="text-[10px] text-gray-400">{fmtDate(d.target_date)}</p>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic py-4 text-center">Aucun delai critique sur vos projets.</p>
          )}
        </div>

        {/* Projets actifs */}
        <div className="realease-card p-5" data-testid="cockpit-projects">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Mes projets</h3>
            <a href="/projects" className="text-xs text-[#008A8D] hover:underline">Tout voir</a>
          </div>
          {projects?.items?.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {projects.items.map(p => (
                <a key={p.project_id} href={`/projects/${p.project_id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors" data-testid={`cockpit-project-${p.project_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                      {p.paused && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Pause</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StatusPill status={p.status} />
                      <span className="text-[10px] text-gray-400">{p.client_name}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-bold text-[#008A8D]">{p.progress || 0}%</p>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic py-4 text-center">Aucun projet ne vous est assigne.</p>
          )}
        </div>
      </div>

      {/* Activite recente */}
      {recent_activity?.length > 0 && (
        <div className="realease-card p-5" data-testid="cockpit-activity">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Activite recente</h3>
            <a href="/activity" className="text-xs text-[#008A8D] hover:underline">Fil complet</a>
          </div>
          <div className="space-y-2">
            {recent_activity.map(n => (
              <a key={n.note_id} href={`/projects/${n.project_id}`} className="block p-2 rounded-lg hover:bg-gray-50">
                <p className="text-sm text-gray-700 line-clamp-2">{n.text}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {n.author_name} · {n.project_name} · {fmtDate(n.created_at)}
                </p>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCockpitWidget;
